import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import api from '../services/api'
import { Plus, Edit, Trash2, Eye, Star, Loader2, Search, X, Check, Info, DollarSign, Users, Image as ImageIcon, Video, Calendar } from 'lucide-react'
import { useState } from 'react'

const AdminProjets = () => {
  const queryClient = useQueryClient()
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedStatut, setSelectedStatut] = useState('')
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [currentProject, setCurrentProject] = useState(null)
  const [activeTab, setActiveTab] = useState('general')
  const [isUploading, setIsUploading] = useState(false)

  const { data, isLoading } = useQuery({
    queryKey: ['admin-projets', selectedStatut],
    queryFn: async () => {
      const res = await api.get('/projets/', { 
        params: { 
          limit: 100,
          statut: selectedStatut || undefined
        } 
      })
      return res.data
    }
  })

  const saveMutation = useMutation({
    mutationFn: (project) => {
      // Ensure JSON fields are arrays if they were edited as strings
      const payload = {
        ...project,
        services_rendus: Array.isArray(project.services_rendus) ? project.services_rendus : project.services_rendus.split(',').map(s => s.trim()).filter(s => s),
        images_realisations: Array.isArray(project.images_realisations) ? project.images_realisations : project.images_realisations,
        videos_realisations: Array.isArray(project.videos_realisations) ? project.videos_realisations : project.videos_realisations.split(',').map(s => s.trim()).filter(s => s),
      }
      delete payload.personnel_liste // Cleaning up old state if present

      if (project.id) {
        return api.put(`/projets/${project.id}`, payload)
      } else {
        const slug = project.slug || project.nom_mission.toLowerCase().replace(/ /g, '-').replace(/[^\w-]+/g, '') + '-' + crypto.randomUUID().split('-')[0]
        return api.post('/projets/', { ...payload, slug })
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['admin-projets'])
      setIsModalOpen(false)
      setCurrentProject(null)
    },
    onError: (error) => {
      console.error("Save project failed", error)
      alert("Erreur lors de l'enregistrement : " + (error.response?.data?.detail || error.message))
    }
  })

  const deleteMutation = useMutation({
    mutationFn: (id) => api.delete(`/projets/${id}`),
    onSuccess: () => queryClient.invalidateQueries(['admin-projets'])
  })

  const toggleFeaturedMutation = useMutation({
    mutationFn: ({ id, epingle }) => api.put(`/projets/${id}`, { epingle_accueil: !epingle }),
    onSuccess: () => queryClient.invalidateQueries(['admin-projets'])
  })

  const handleEdit = (project) => {
    setCurrentProject({
      ...project,
      personnel_liste: project.personnel_liste?.join(', ') || '',
      services_rendus: project.services_rendus?.join(', ') || '',
      images_realisations: project.images_realisations || [],
      videos_realisations: project.videos_realisations?.join(', ') || '',
      date_demarrage: project.date_demarrage || '',
      date_achevement: project.date_achevement || ''
    })
    setActiveTab('general')
    setIsModalOpen(true)
  }

  const handleCreate = () => {
    setCurrentProject({
      nom_mission: '',
      valeur_contrat_fcfa: 0,
      pays: '',
      lieu: '',
      duree_mission_mois: 0,
      autorite_contractante: '',
      nb_employes_total: 0,
      adresse: '',
      valeur_services_fcfa: 0,
      date_demarrage: '',
      date_achevement: '',
      nb_employes_associes: 0,
      consultants_associes: '',
      cadres_professionnels: '',
      description_projet: '',
      services_rendus: '',
      miniature_image: '',
      miniature_video: '',
      images_realisations: [],
      videos_realisations: '',
      secteur: '',
      region: '',
      statut: 'en_cours',
      epingle_accueil: false
    })
    setActiveTab('general')
    setIsModalOpen(true)
  }

  const handleFileChange = async (e) => {
    const file = e.target.files[0]
    if (!file) return

    setIsUploading(true)
    const formData = new FormData()
    formData.append('file', file)

    try {
      const res = await api.post('/medias/upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      })
      setCurrentProject({ ...currentProject, miniature_image: res.data.chemin })
    } catch (error) {
      console.error("Upload failed", error)
      alert("Échec de l'upload de l'image")
    } finally {
      setIsUploading(false)
    }
  }

  const handleMultipleFilesChange = async (e) => {
    const files = Array.from(e.target.files)
    if (files.length === 0) return

    setIsUploading(true)
    const uploadedPaths = []

    try {
      for (const file of files) {
        const formData = new FormData()
        formData.append('file', file)
        const res = await api.post('/medias/upload', formData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        })
        uploadedPaths.push(res.data.chemin)
      }
      setCurrentProject({ 
        ...currentProject, 
        images_realisations: [...(currentProject.images_realisations || []), ...uploadedPaths] 
      })
    } catch (error) {
      console.error("Multiple upload failed", error)
      alert("Échec de l'upload de certaines images")
    } finally {
      setIsUploading(false)
    }
  }

  const removeImageRealisation = (index) => {
    const newImages = [...currentProject.images_realisations]
    newImages.splice(index, 1)
    setCurrentProject({ ...currentProject, images_realisations: newImages })
  }

  const filteredProjets = (data?.items || []).filter(p => 
    p.nom_mission.toLowerCase().includes(searchTerm.toLowerCase()) ||
    p.autorite_contractante?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    p.secteur?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    p.pays?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    p.lieu?.toLowerCase().includes(searchTerm.toLowerCase())
  ).sort((a, b) => b.id - a.id)

  if (isLoading) return <div className="flex justify-center py-20"><Loader2 className="animate-spin text-cidh-gold" size={48} /></div>

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h3 className="text-2xl font-bold text-cidh-blue font-montserrat">Gestion des Projets</h3>
          <p className="text-gray-500">Gérez l'ensemble des missions et projets</p>
        </div>
        <button 
          onClick={handleCreate}
          className="btn-primary flex items-center justify-center gap-2 py-3 px-6 rounded-xl shadow-lg shadow-cidh-gold/20 transition-all hover:scale-105 active:scale-95"
        >
          <Plus size={20} /> Nouveau Projet
        </button>
      </div>

      <div className="bg-white p-4 rounded-2xl shadow-sm border border-gray-100 flex flex-col md:flex-row gap-4">
        <div className="relative flex-grow">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
          <input 
            type="text" 
            placeholder="Rechercher par titre ou secteur..." 
            className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-xl focus:ring-2 focus:ring-cidh-gold/20 focus:border-cidh-gold outline-none transition-all"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <div className="flex gap-2">
          <select 
            className="px-4 py-2 border border-gray-200 rounded-xl focus:ring-2 focus:ring-cidh-gold/20 focus:border-cidh-gold outline-none transition-all bg-white"
            value={selectedStatut}
            onChange={(e) => setSelectedStatut(e.target.value)}
          >
            <option value="">Tous les statuts</option>
            <option value="en_cours">En cours</option>
            <option value="archive">Achevé / Archivé</option>
          </select>
        </div>
      </div>

      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left">
            <thead className="bg-gray-50 text-gray-500 text-xs uppercase font-bold">
              <tr>
                <th className="px-6 py-4">Mission</th>
                <th className="px-6 py-4">Pays / Lieu</th>
                <th className="px-6 py-4">Autorité Contractante</th>
                <th className="px-6 py-4">Valeur (F CFA)</th>
                <th className="px-6 py-4">Statut</th>
                <th className="px-6 py-4 text-center">Épingle</th>
                <th className="px-6 py-4 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filteredProjets?.map((projet) => (
                <tr key={projet.id} className="hover:bg-gray-50/50 transition-colors group">
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 rounded-lg overflow-hidden border border-gray-100 flex-shrink-0">
                        <img 
                          src={projet.miniature_image ? `/media/${projet.miniature_image}` : 'https://placehold.co/48x48?text=Projet'} 
                          className="w-full h-full object-cover"
                          alt={projet.nom_mission}
                        />
                      </div>
                      <div className="max-w-[300px]">
                        <div className="font-bold text-cidh-blue text-sm line-clamp-2">{projet.nom_mission}</div>
                        <div className="text-xs text-cidh-gold font-medium mt-1">{projet.secteur || 'Divers'}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm font-medium text-gray-700">{projet.pays || '-'}</div>
                    <div className="text-xs text-gray-500">{projet.lieu || '-'}</div>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-500 max-w-[200px] truncate">
                    {projet.autorite_contractante || '-'}
                  </td>
                  <td className="px-6 py-4 text-sm font-bold text-gray-700">
                    {projet.valeur_contrat_fcfa?.toLocaleString('fr-FR')} 
                  </td>
                  <td className="px-6 py-4">
                      {projet.statut === 'archive' ? 'Archivé' : projet.statut.replace('_', ' ')}
                  </td>
                  <td className="px-6 py-4 text-center">
                    <button 
                      onClick={() => toggleFeaturedMutation.mutate({ id: projet.id, epingle: projet.epingle_accueil })}
                      className={`transition-colors ${projet.epingle_accueil ? 'text-cidh-gold' : 'text-gray-300 hover:text-cidh-gold'}`}
                    >
                      <Star fill={projet.epingle_accueil ? 'currentColor' : 'none'} size={20} className="mx-auto" />
                    </button>
                  </td>
                  <td className="px-6 py-4 text-right">
                    <div className="flex justify-end gap-1 opacity-100 md:opacity-0 group-hover:opacity-100 transition-opacity">
                      <a href={`/projets/${projet.slug}`} target="_blank" rel="noreferrer" className="p-2 text-blue-500 hover:bg-blue-50 rounded-lg transition-colors">
                        <Eye size={18} />
                      </a>
                      <button onClick={() => handleEdit(projet)} className="p-2 text-cidh-gold hover:bg-gold-50 rounded-lg transition-colors">
                        <Edit size={18} />
                      </button>
                      <button 
                        onClick={() => window.confirm('Supprimer ce projet ?') && deleteMutation.mutate(projet.id)}
                        className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                      >
                        <Trash2 size={18} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Exhaustive Modal Form */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-start justify-center p-4 bg-black/50 backdrop-blur-sm overflow-y-auto pt-10">
          <div className="bg-white rounded-3xl w-full max-w-4xl max-h-[90vh] flex flex-col shadow-2xl animate-scale-in overflow-hidden">
            <div className="p-6 border-b flex justify-between items-center bg-white sticky top-0 z-10">
              <h4 className="text-xl font-bold text-cidh-blue font-montserrat">
                {currentProject?.id ? 'Modifier le Projet' : 'Nouveau Projet'}
              </h4>
              <button onClick={() => setIsModalOpen(false)} className="p-2 hover:bg-gray-100 rounded-full"><X size={24} /></button>
            </div>
            
            <div className="flex border-b bg-gray-50/50 overflow-x-auto whitespace-nowrap scrollbar-hide">
              <button 
                onClick={() => setActiveTab('general')}
                className={`flex items-center gap-2 px-6 py-4 font-bold text-sm transition-all border-b-2 flex-shrink-0 ${activeTab === 'general' ? 'border-cidh-gold text-cidh-blue bg-white' : 'border-transparent text-gray-500 hover:text-cidh-blue'}`}
              >
                <Info size={18} /> Général
              </button>
              <button 
                onClick={() => setActiveTab('values')}
                className={`flex items-center gap-2 px-6 py-4 font-bold text-sm transition-all border-b-2 flex-shrink-0 ${activeTab === 'values' ? 'border-cidh-gold text-cidh-blue bg-white' : 'border-transparent text-gray-500 hover:text-cidh-blue'}`}
              >
                <DollarSign size={18} /> Valeurs & Dates
              </button>
              <button 
                onClick={() => setActiveTab('team')}
                className={`flex items-center gap-2 px-6 py-4 font-bold text-sm transition-all border-b-2 flex-shrink-0 ${activeTab === 'team' ? 'border-cidh-gold text-cidh-blue bg-white' : 'border-transparent text-gray-500 hover:text-cidh-blue'}`}
              >
                <Users size={18} /> Équipe & Services
              </button>
              <button 
                onClick={() => setActiveTab('media')}
                className={`flex items-center gap-2 px-6 py-4 font-bold text-sm transition-all border-b-2 flex-shrink-0 ${activeTab === 'media' ? 'border-cidh-gold text-cidh-blue bg-white' : 'border-transparent text-gray-500 hover:text-cidh-blue'}`}
              >
                <ImageIcon size={18} /> Médias
              </button>
            </div>

            <form id="project-form" className="flex-grow overflow-y-auto p-8" onSubmit={(e) => {
              e.preventDefault()
              saveMutation.mutate(currentProject)
            }}>
              {activeTab === 'general' && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 animate-fade-in">
                  <div className="space-y-2 md:col-span-2">
                    <label className="text-xs font-bold text-gray-500 uppercase">Nom de la Mission *</label>
                    <input type="text" required className="input-field" value={currentProject?.nom_mission} onChange={(e) => setCurrentProject({...currentProject, nom_mission: e.target.value})} />
                  </div>
                  <div className="space-y-2">
                    <label className="text-xs font-bold text-gray-500 uppercase">Pays</label>
                    <input type="text" className="input-field" value={currentProject?.pays} onChange={(e) => setCurrentProject({...currentProject, pays: e.target.value})} />
                  </div>
                  <div className="space-y-2">
                    <label className="text-xs font-bold text-gray-500 uppercase">Lieu</label>
                    <input type="text" className="input-field" value={currentProject?.lieu} onChange={(e) => setCurrentProject({...currentProject, lieu: e.target.value})} />
                  </div>
                  <div className="space-y-2">
                    <label className="text-xs font-bold text-gray-500 uppercase">Statut</label>
                    <select className="input-field" value={currentProject?.statut} onChange={(e) => setCurrentProject({...currentProject, statut: e.target.value})}>
                      <option value="en_cours">En cours</option>
                      <option value="archive">Achevé / Archivé</option>
                    </select>
                  </div>
                  <div className="space-y-2">
                    <label className="text-xs font-bold text-gray-500 uppercase">Secteur</label>
                    <input type="text" className="input-field" value={currentProject?.secteur} onChange={(e) => setCurrentProject({...currentProject, secteur: e.target.value})} />
                  </div>
                  <div className="space-y-2">
                    <label className="text-xs font-bold text-gray-500 uppercase">Région</label>
                    <input type="text" className="input-field" value={currentProject?.region} onChange={(e) => setCurrentProject({...currentProject, region: e.target.value})} />
                  </div>
                  <div className="space-y-2">
                     <label className="text-xs font-bold text-gray-500 uppercase">Nom de l’Autorité contractante</label>
                     <input type="text" className="input-field" value={currentProject?.autorite_contractante} onChange={(e) => setCurrentProject({...currentProject, autorite_contractante: e.target.value})} />
                  </div>
                  <div className="space-y-2 md:col-span-2">
                     <label className="text-xs font-bold text-gray-500 uppercase">Adresse</label>
                     <input type="text" className="input-field" value={currentProject?.adresse} onChange={(e) => setCurrentProject({...currentProject, adresse: e.target.value})} />
                  </div>
                  <div className="space-y-2 md:col-span-2">
                    <label className="text-xs font-bold text-gray-500 uppercase">Nom des cadres professionnels de notre Société employés et fonctions exécutées</label>
                    <textarea rows="3" className="input-field resize-none" value={currentProject?.cadres_professionnels} onChange={(e) => setCurrentProject({...currentProject, cadres_professionnels: e.target.value})}></textarea>
                  </div>
                  <div className="space-y-2 md:col-span-2">
                    <label className="text-xs font-bold text-gray-500 uppercase">Description du projet</label>
                    <textarea rows="4" className="input-field resize-none" value={currentProject?.description_projet} onChange={(e) => setCurrentProject({...currentProject, description_projet: e.target.value})}></textarea>
                  </div>
                  <div className="flex items-center gap-2">
                    <input type="checkbox" id="epingle" checked={currentProject?.epingle_accueil} onChange={(e) => setCurrentProject({...currentProject, epingle_accueil: e.target.checked})} className="w-5 h-5 accent-cidh-gold" />
                    <label htmlFor="epingle" className="text-sm font-medium text-gray-700">Épingler sur la page d'accueil</label>
                  </div>
                </div>
              )}

              {activeTab === 'values' && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 animate-fade-in">
                  <div className="space-y-2">
                    <label className="text-xs font-bold text-gray-500 uppercase">Valeur approximative du contrat (en F CFA)</label>
                    <input type="number" className="input-field" value={currentProject?.valeur_contrat_fcfa} onChange={(e) => setCurrentProject({...currentProject, valeur_contrat_fcfa: parseFloat(e.target.value) || 0})} />
                  </div>
                  <div className="space-y-2">
                    <label className="text-xs font-bold text-gray-500 uppercase">Valeur approximative des services offerts par notre société (en F CFA)</label>
                    <input type="number" className="input-field" value={currentProject?.valeur_services_fcfa} onChange={(e) => setCurrentProject({...currentProject, valeur_services_fcfa: parseFloat(e.target.value) || 0})} />
                  </div>
                  <div className="space-y-2">
                    <label className="text-xs font-bold text-gray-500 uppercase">Durée de la mission (mois)</label>
                    <input type="number" className="input-field" value={currentProject?.duree_mission_mois} onChange={(e) => setCurrentProject({...currentProject, duree_mission_mois: parseInt(e.target.value) || 0})} />
                  </div>
                  <div className="space-y-2">
                    <label className="text-xs font-bold text-gray-500 uppercase">Date de démarrage (mois/année)</label>
                    <input type="text" className="input-field" placeholder="ex: Octobre 2022" value={currentProject?.date_demarrage} onChange={(e) => setCurrentProject({...currentProject, date_demarrage: e.target.value})} />
                  </div>
                  <div className="space-y-2">
                    <label className="text-xs font-bold text-gray-500 uppercase">Date d'achèvement (mois/année)</label>
                    <input type="text" className="input-field" placeholder="ex: Mars 2024" value={currentProject?.date_achevement} onChange={(e) => setCurrentProject({...currentProject, date_achevement: e.target.value})} />
                  </div>
                </div>
              )}

              {activeTab === 'team' && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 animate-fade-in">
                  <div className="space-y-2">
                    <label className="text-xs font-bold text-gray-500 uppercase">Nombre total d'employés/mois ayant participé à la Mission</label>
                    <input type="number" className="input-field" value={currentProject?.nb_employes_total} onChange={(e) => setCurrentProject({...currentProject, nb_employes_total: parseInt(e.target.value) || 0})} />
                  </div>
                  <div className="space-y-2">
                    <label className="text-xs font-bold text-gray-500 uppercase">Nombre d'employés/mois fournis par les consultants associés</label>
                    <input type="number" className="input-field" value={currentProject?.nb_employes_associes} onChange={(e) => setCurrentProject({...currentProject, nb_employes_associes: parseInt(e.target.value) || 0})} />
                  </div>
                  <div className="space-y-2 md:col-span-2">
                    <label className="text-xs font-bold text-gray-500 uppercase">Noms des consultants associés/partenaires éventuels</label>
                    <input type="text" className="input-field" value={currentProject?.consultants_associes} onChange={(e) => setCurrentProject({...currentProject, consultants_associes: e.target.value})} />
                  </div>
                  <div className="space-y-2 md:col-span-2">
                    <label className="text-xs font-bold text-gray-500 uppercase">Description des services effectivement rendus par notre personnel (un service par ligne)</label>
                    <textarea rows="3" className="input-field resize-none" value={currentProject?.services_rendus} onChange={(e) => setCurrentProject({...currentProject, services_rendus: e.target.value})} placeholder="Audit&#10;Conseil&#10;Formation"></textarea>
                  </div>
                </div>
              )}

              {activeTab === 'media' && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 animate-fade-in">
                  <div className="space-y-2 md:col-span-2">
                    <label className="text-xs font-bold text-gray-500 uppercase">Imedia (Images et Vidéos) - Miniature Image *</label>
                    <div className="flex items-center gap-4">
                      {currentProject?.miniature_image && (
                        <div className="w-16 h-16 rounded-xl overflow-hidden border border-gray-100 flex-shrink-0 bg-gray-50">
                          <img 
                            src={`/media/${currentProject.miniature_image}`} 
                            className="w-full h-full object-cover" 
                            alt="Prévisualisation"
                          />
                        </div>
                      )}
                      <div className="flex-grow">
                        <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed border-gray-200 rounded-2xl cursor-pointer hover:bg-gray-50 hover:border-cidh-gold/50 transition-all group">
                          <div className="flex flex-col items-center justify-center pt-5 pb-6">
                            {isUploading ? (
                              <Loader2 className="animate-spin text-cidh-gold mb-2" size={24} />
                            ) : (
                              <ImageIcon className="text-gray-400 group-hover:text-cidh-gold mb-2" size={24} />
                            )}
                            <p className="text-xs text-gray-500 group-hover:text-cidh-gold"><span className="font-bold">Déposez la miniature</span> ou cliquez</p>
                            <p className="text-[10px] text-gray-400 mt-1">PNG, JPG (Max. 5MB)</p>
                          </div>
                          <input type="file" className="hidden" accept="image/*" onChange={handleFileChange} />
                        </label>
                      </div>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <label className="text-xs font-bold text-gray-500 uppercase">Miniature Vidéo (Lien)</label>
                    <input type="text" className="input-field" value={currentProject?.miniature_video} onChange={(e) => setCurrentProject({...currentProject, miniature_video: e.target.value})} />
                  </div>
                  <div className="space-y-4 md:col-span-2">
                    <label className="text-xs font-bold text-gray-500 uppercase">Images Réalisations</label>
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
                      {currentProject?.images_realisations?.map((img, index) => (
                        <div key={index} className="relative group aspect-video rounded-xl overflow-hidden border border-gray-100 bg-gray-50">
                          <img 
                            src={`/media/${img}`} 
                            className="w-full h-full object-cover" 
                            alt={`Réalisation ${index + 1}`}
                          />
                          <button 
                            type="button"
                            onClick={() => removeImageRealisation(index)}
                            className="absolute top-1 right-1 p-1.5 bg-red-500 text-white rounded-full opacity-100 md:opacity-0 group-hover:opacity-100 transition-opacity shadow-lg"
                          >
                            <X size={14} />
                          </button>
                        </div>
                      ))}
                      <label className="flex flex-col items-center justify-center aspect-video border-2 border-dashed border-gray-200 rounded-xl cursor-pointer hover:bg-gray-50 hover:border-cidh-gold/50 transition-all group">
                        <div className="flex flex-col items-center justify-center p-2">
                          {isUploading ? (
                            <Loader2 className="animate-spin text-cidh-gold" size={20} />
                          ) : (
                            <Plus className="text-gray-400 group-hover:text-cidh-gold" size={20} />
                          )}
                          <span className="text-[10px] font-bold text-gray-400 group-hover:text-cidh-gold mt-1">Ajouter</span>
                        </div>
                        <input type="file" className="hidden" accept="image/*" multiple onChange={handleMultipleFilesChange} />
                      </label>
                    </div>
                  </div>
                  <div className="space-y-2 md:col-span-2">
                    <label className="text-xs font-bold text-gray-500 uppercase">Vidéos Réalisations (Liens séparés par des virgules)</label>
                    <textarea rows="3" className="input-field resize-none" value={currentProject?.videos_realisations} onChange={(e) => setCurrentProject({...currentProject, videos_realisations: e.target.value})}></textarea>
                  </div>
                </div>
              )}
            </form>

            <div className="p-6 border-t flex justify-end gap-4 bg-white sticky bottom-0 z-10">
              <button type="button" onClick={() => setIsModalOpen(false)} className="px-6 py-3 border border-gray-200 rounded-xl font-bold text-gray-400 hover:bg-gray-50 transition-all">Annuler</button>
              <button 
                form="project-form"
                type="submit"
                disabled={saveMutation.isPending || isUploading}
                className="btn-primary flex items-center gap-2 py-3 px-10 rounded-xl shadow-lg shadow-cidh-gold/20 transition-all active:scale-95 disabled:opacity-50"
              >
                {saveMutation.isPending || isUploading ? <Loader2 className="animate-spin" size={20} /> : <Check size={20} />}
                Enregistrer
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Internal CSS for consistent fields */}
      <style>{`
        .input-field {
          width: 100%;
          padding: 0.75rem 1rem;
          border: 1px solid #E5E7EB;
          border-radius: 0.75rem;
          background-color: white;
          transition: all 0.2s;
          font-size: 0.875rem;
        }
        .input-field:focus {
          outline: none;
          border-color: #D4AF37;
          box-shadow: 0 0 0 3px rgba(212, 175, 55, 0.1);
        }
      `}</style>
    </div>
  )
}

export default AdminProjets
